/**
 * LookupFieldControl
 * Renders a searchable combobox backed by another form's records.
 * Used for `field_type === "lookup"`.
 *
 * Props:
 *  - field: the FormField metadata (must have lookup_form_id + lookup_display_field)
 *  - value: current stored value (typically the target record's id)
 *  - onChange(v)
 *  - disabled
 *  - testid
 *  - parentValue: value of the parent field (when this lookup is dependent)
 *
 * Behavior:
 *  - Fetches options from /api/lookup/{form_id}/options on mount + search
 *  - Caches the label for the current value so we can display human text
 *  - If `lookup_filter_field` is set and parentValue is empty → disable + hint
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, CaretDown, Check, X, Link as LinkIcon } from "@phosphor-icons/react";

export default function LookupFieldControl({ field, value, onChange, disabled, testid, parentValue }) {
    const targetFormId = field.lookup_form_id;
    const displayField = field.lookup_display_field;
    const valueField = field.lookup_value_field || "id";
    const filterField = field.lookup_filter_target_field;
    const requiresParent = !!field.lookup_filter_field;

    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const containerRef = useRef(null);

    const dependentBlocked = requiresParent && (parentValue == null || parentValue === "");

    const fetchOptions = useMemo(() => async (searchTerm) => {
        if (!targetFormId || !displayField) return;
        if (dependentBlocked) { setItems([]); return; }
        setLoading(true);
        try {
            const params = { display_field: displayField, value_field: valueField, limit: 50 };
            if (searchTerm) params.q = searchTerm;
            if (filterField && parentValue !== "" && parentValue != null) {
                params.filter_field = filterField;
                params.filter_value = parentValue;
            }
            const { data } = await api.get(`/lookup/${targetFormId}/options`, { params });
            setItems(data?.items || []);
        } catch (e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [targetFormId, displayField, valueField, filterField, parentValue, dependentBlocked]);

    useEffect(() => {
        if (open) fetchOptions(q);
    }, [open, q, fetchOptions]);

    // Resolve the label for the currently-stored value (one-shot)
    useEffect(() => {
        let active = true;
        if (!value) { setSelectedLabel(null); return; }
        // Try to find within already-loaded items first
        const found = items.find((i) => String(i.value) === String(value));
        if (found) { setSelectedLabel(found.label); return; }
        // Otherwise, fire a small fetch with no search to get the record by id
        (async () => {
            if (!targetFormId || !displayField) return;
            try {
                const { data } = await api.get(`/lookup/${targetFormId}/options`, {
                    params: { display_field: displayField, value_field: valueField, limit: 200 },
                });
                if (!active) return;
                const f2 = (data?.items || []).find((i) => String(i.value) === String(value));
                if (f2) setSelectedLabel(f2.label);
            } catch { /* swallow */ }
        })();
        return () => { active = false; };
    }, [value, targetFormId, displayField, valueField, items]);

    // Close on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    if (!targetFormId || !displayField) {
        return (
            <div className="border border-dashed border-border rounded-sm p-3 text-xs text-muted-foreground" data-testid={testid}>
                <LinkIcon size={12} className="inline mr-1" />
                Lookup not configured. Edit the field to pick a target form &amp; display column.
            </div>
        );
    }

    return (
        <div ref={containerRef} className="relative" data-testid={testid}>
            <button
                type="button"
                disabled={disabled || dependentBlocked}
                onClick={() => setOpen((v) => !v)}
                className="w-full border border-border rounded-sm px-3 py-2 text-left text-sm bg-card hover:bg-secondary/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-2"
                data-testid={`${testid}-trigger`}
            >
                <span className={value ? "" : "text-muted-foreground"}>
                    {dependentBlocked ? (
                        <em className="text-xs">Pick the parent field first</em>
                    ) : value ? (
                        selectedLabel || <code className="font-mono text-xs">{String(value).slice(0, 16)}…</code>
                    ) : (
                        <span>Search records…</span>
                    )}
                </span>
                <div className="flex items-center gap-1">
                    {value && !disabled && !dependentBlocked && (
                        <X size={12} className="text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); onChange?.(""); setSelectedLabel(null); }} />
                    )}
                    <CaretDown size={12} className="text-muted-foreground" />
                </div>
            </button>

            {open && !dependentBlocked && (
                <div className="absolute z-30 mt-1 w-full bg-card border border-border rounded-sm shadow-md max-h-72 overflow-hidden flex flex-col">
                    <div className="relative p-2 border-b border-border">
                        <MagnifyingGlass size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={`Search ${field.field_label || displayField}…`}
                            className="rounded-sm pl-7 h-8 text-sm"
                            data-testid={`${testid}-search`}
                        />
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {loading && <div className="p-3 text-xs text-muted-foreground">Loading…</div>}
                        {!loading && items.length === 0 && (
                            <div className="p-3 text-xs text-muted-foreground">No matching records.</div>
                        )}
                        {items.map((opt) => {
                            const sel = String(opt.value) === String(value);
                            return (
                                <button
                                    type="button"
                                    key={opt.value}
                                    onClick={() => { onChange?.(opt.value); setSelectedLabel(opt.label); setOpen(false); }}
                                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-secondary flex items-center justify-between ${sel ? "bg-secondary/60" : ""}`}
                                    data-testid={`${testid}-opt-${opt.value}`}
                                >
                                    <span className="truncate">{opt.label}</span>
                                    {sel && <Check size={12} className="text-primary" />}
                                </button>
                            );
                        })}
                    </div>
                    <div className="px-3 py-1.5 border-t border-border text-[10px] text-muted-foreground font-mono">
                        {items.length} option{items.length === 1 ? "" : "s"}{filterField ? <Badge variant="outline" className="ml-2 rounded-sm text-[9px]">filtered</Badge> : null}
                    </div>
                </div>
            )}
        </div>
    );
}
